import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Accordion, AccordionItem } from "react-light-accordion";
import Header from "./components/Header";
import { Footer } from "./components/Footer";
import { Jujharcont } from "./components/Jujharcont";
import { ErrorPage } from "./ErrorPage";
import Feature01 from "./images/Feature_img_01.webp";
import Feature02 from "./images/Feature_img_02.webp";
import Feature03 from "./images/Feature_img_3.webp";
import Feature04 from "./images/Feature_img_4.webp";
import cities from "./data/cities.json";
import "./css/CityBroadband.css";

// The plans slider calls the API and pulls in the slider library, so it is split out
// and only loaded when the visitor scrolls near it (keeps the first paint fast).
const Broadband = lazy(() =>
  import("./components/Broadband").then((m) => ({ default: m.Broadband })),
);

// Mounts children only once the placeholder is close to the viewport.
const LazyMount = ({ children, minHeight = 400, rootMargin = "300px" }) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (show) return;
    if (!("IntersectionObserver" in window)) {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [show, rootMargin]);
  return (
    <div ref={ref} style={{ minHeight: show ? undefined : minHeight }}>
      {show ? (
        <Suspense fallback={<div style={{ minHeight }} />}>{children}</Suspense>
      ) : null}
    </div>
  );
};

const SITE = "https://www.netplus.co.in";
const PHONE_DISPLAY = "70875-70875";
const PHONE_TEL = "+917087570875";
const EMAIL = "customercare@netplus.co.in";
const BOOK_LINK = "/book-now/bVhQYTVDZ1hCN3JkWnppc0g2N2lidz09";
// Update this when the tariff changes (it appears in the page text and the meta description).
const PRICE_FROM = "₹449";

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const buildFaqs = (c) => {
  const faqs = [
    {
      q: `What WiFi broadband plans does Netplus offer in ${c.name}?`,
      a: `Netplus offers fiber broadband plans in ${c.name} from ${PRICE_FROM} per month, with speeds from 75 Mbps up to 1 Gbps and unlimited data. The current plans and prices are listed on this page. Availability depends on your address.`,
    },
    {
      q: `How do I get a new Netplus connection in ${c.name}?`,
      a: `Choose a plan on this page and use the Book Now option to submit your details, or call ${PHONE_DISPLAY}. A Netplus executive will contact you to confirm availability and installation.`,
    },
    {
      q: `Is Netplus available at my address in ${c.name}?`,
      a:
        `Availability depends on the exact location. Use the Book Now option or call ${PHONE_DISPLAY} and Netplus will confirm for your address.` +
        (c.localities && c.localities.length
          ? ` Netplus currently serves areas in ${c.name} including ${c.localities.join(", ")}.`
          : ""),
    },
    {
      q: `Where is the Netplus office in ${c.name}?`,
      a: `The Netplus office in ${c.name} is at ${c.office}. You can also call ${PHONE_DISPLAY} or email ${EMAIL}.`,
    },
    {
      q: `Does Netplus offer IPTV and OTT along with broadband in ${c.name}?`,
      a: `Yes. Netplus offers IPTV and OTT combo plans that bundle broadband, live TV channels and OTT apps. See the IPTV plans page for the current options.`,
    },
    {
      q: `Do Netplus broadband plans include unlimited data?`,
      a: `Yes. The broadband plans currently listed on the Netplus website include unlimited data. Check the individual plan details for the latest terms and benefits.`,
    },
  ];
  if (c.installTime) {
    faqs.push({
      q: `How long does it take to get a Netplus connection in ${c.name}?`,
      a: c.installTime,
    });
  }
  return faqs;
};

const benefits = [
  {
    img: Feature01,
    title: "High speed Internet",
    text: "Fiber broadband with speeds from 75 Mbps up to 1 Gbps.",
  },
  {
    img: Feature02,
    title: "Local support",
    text:
      "A Netplus office in the city and one helpline: " + PHONE_DISPLAY + ".",
  },
  {
    img: Feature03,
    title: "Entertainment packages",
    text: "Combine broadband with live TV channels and OTT apps in one plan.",
  },
  {
    img: Feature04,
    title: "Unlimited data",
    text: "The plans listed on this page include unlimited data.",
  },
];

export const CityBroadband = ({ match, history }) => {
  const slug = ((match.params && match.params.city) || "").toLowerCase();
  const [pick, setPick] = useState(slug);

  // keep the dropdown in sync when the visitor moves to another city page
  useEffect(() => {
    setPick(slug);
  }, [slug]);

  if (!has(cities, slug)) {
    return <ErrorPage />;
  }

  const c = cities[slug];
  const url = `${SITE}/wifi-service-provider/${slug}/`;
  const title = `Best WiFi Broadband in ${c.name} | Netplus Broadband`;
  const description = `Netplus Broadband in ${c.name}: high-speed fiber broadband, IPTV & OTT plans from ${PRICE_FROM}/month with unlimited data. Book online or call ${PHONE_DISPLAY}.`;
  const faqs = buildFaqs(c);

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `Netplus Broadband - ${c.name}`,
    url,
    telephone: PHONE_TEL,
    email: EMAIL,
    address: {
      "@type": "PostalAddress",
      streetAddress: c.office,
      addressLocality: c.name,
      addressCountry: "IN",
    },
    areaServed: { "@type": "City", name: c.name },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE },
      {
        "@type": "ListItem",
        position: 2,
        name: "WiFi service provider",
        item: `${SITE}/wifi-service-provider`,
      },
      { "@type": "ListItem", position: 3, name: c.name, item: url },
    ],
  };

  const otherCities = Object.keys(cities).filter((k) => k !== slug);

  const goToCity = (e) => {
    e.preventDefault();
    if (pick && pick !== slug) history.push(`/wifi-service-provider/${pick}`);
    else history.push(BOOK_LINK);
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {/* delete any <link rel="canonical"> and og:url here; Seo handles them */}
      </Helmet>

      <Header />

      <main className="cb">
        {/* HERO - plain, light, no video: paints immediately */}
        <section className="cb-hero">
          <div className="cb-wrap cb-hero-grid">
            <div>
              <p className="cb-crumbs">
                <Link to="/">Home</Link> / WiFi service provider / {c.name}
              </p>
              <h1>
                High-speed WiFi broadband with Netplus in <em>{c.name}</em>
              </h1>
              <p className="cb-lead">
                Fiber broadband, IPTV and OTT plans in {c.name} from{" "}
                {PRICE_FROM} a month. Speeds from 75 Mbps to 1 Gbps, with
                unlimited data.
              </p>
              {c.offer ? <p className="cb-offer">{c.offer}</p> : null}
              <div className="cb-actions">
                <Link to={BOOK_LINK} className="cb-btn">
                  Book now
                </Link>
                <a href={`tel:${PHONE_TEL}`} className="cb-call">
                  Call {PHONE_DISPLAY}
                </a>
              </div>
              <ul className="cb-facts">
                <li>
                  <strong>{PRICE_FROM}/mo</strong>starting price
                </li>
                <li>
                  <strong>75 Mbps–1 Gbps</strong>speed range
                </li>
                <li>
                  <strong>Unlimited</strong>data on listed plans
                </li>
              </ul>
            </div>

            <form className="cb-card" onSubmit={goToCity}>
              <h2>Check availability in your city</h2>
              <p>
                Select your city, or book now and we will confirm your address.
              </p>
              <label htmlFor="cb-city">City</label>
              <select
                id="cb-city"
                className="cb-select"
                value={pick}
                onChange={(e) => setPick(e.target.value)}
              >
                {Object.keys(cities).map((k) => (
                  <option key={k} value={k}>
                    {cities[k].name}
                  </option>
                ))}
              </select>
              <button type="submit" className="cb-btn">
                {pick === slug
                  ? `Book in ${c.name}`
                  : "Get Connection in your city"}
              </button>
              <small>
                Helpline {PHONE_DISPLAY} · {EMAIL}
              </small>
            </form>
          </div>
        </section>

        {/* PLANS - exact same markup as the Broadband plans page; slider loads when scrolled near */}
        <section id="Broadband" className="blckarrow">
          <div className="container">
            <div className="row">
              <div
                className="title"
                data-aos="fade-up"
                data-aos-offset="200"
                data-aos-duration="600"
                data-aos-once="true"
                data-aos-easing="ease-in-sine"
              >
                <span>Broadband Plans</span>
                <h3>Your need. Your choice.</h3>
              </div>
              <LazyMount minHeight={620}>
                <Broadband />
              </LazyMount>
            </div>
          </div>
        </section>

        <section className="cb-sec">
          <div className="cb-wrap">
            <h2 className="cb-h">Why choose Netplus in {c.name}</h2>
            <div className="cb-benefits" style={{ marginTop: 24 }}>
              {benefits.map((b) => (
                <div className="cb-benefit" key={b.title}>
                  <img
                    src={b.img}
                    alt=""
                    width="56"
                    height="56"
                    loading="lazy"
                    decoding="async"
                  />
                  <h3>{b.title}</h3>
                  <p>{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="cb-sec alt">
          <div className="cb-wrap cb-office">
            <div>
              <h2 className="cb-h">Netplus office in {c.name}</h2>
              <p>{c.office}</p>
              <p>
                Call <a href={`tel:${PHONE_TEL}`}>{PHONE_DISPLAY}</a> or email{" "}
                <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
              </p>
            </div>
            <div>
              {c.localities && c.localities.length ? (
                <>
                  <h3>Areas we serve in {c.name}</h3>
                  <div className="cb-chips">
                    {c.localities.map((l) => (
                      <span className="cb-chip" key={l}>
                        {l}
                      </span>
                    ))}
                  </div>
                </>
              ) : null}
              <h3 style={{ marginTop: 24 }}>Netplus in other cities</h3>
              <div className="cb-chips">
                {otherCities.map((k) => (
                  <Link
                    className="cb-chip"
                    key={k}
                    to={`/wifi-service-provider/${k}`}
                  >
                    {cities[k].name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="cb-sec cb-faq" id="faq">
          <div className="cb-wrap">
            <h2 className="cb-h">
              Netplus in {c.name}: frequently asked questions
            </h2>
            <div className="faqcont" style={{ marginTop: 24 }}>
              <Accordion atomic={true}>
                {faqs.map((f, i) => (
                  <AccordionItem key={i} title={`${i + 1}. ${f.q}`}>
                    <div className="accordiancont">
                      <p>{f.a}</p>
                    </div>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      </main>

      <Jujharcont />
      <Footer />
    </>
  );
};
